import { apiService } from './index';
import { TOKEN } from '../contexts/authContext';
import { LOCALHOST, SIGNIN } from '../routes/paths';
import {
    IExportTemplateIvrTypificationBase,
    ISelectIvrTypificationBase,
} from '../shared/types/ivrTypificationBase';

/** Texto legible a partir de la respuesta de error del backend (import IVR base). */
export const formatIvrTypificationBaseImportErrorPayload = (
    body: unknown
): string => {
    if (!body || typeof body !== 'object') {
        return 'Error al importar';
    }
    const b = body as Record<string, unknown>;
    const parts: string[] = [];

    const msg = b.message;
    if (typeof msg === 'string' && msg.trim()) {
        parts.push(msg.trim());
    } else if (Array.isArray(msg)) {
        msg.forEach((m) => parts.push(String(m)));
    }

    const errs = b.errors;
    if (errs && typeof errs === 'object' && !Array.isArray(errs)) {
        Object.entries(errs as Record<string, unknown>).forEach(
            ([code, val]) => {
                if (Array.isArray(val)) {
                    val.forEach((line) =>
                        parts.push(`${code}: ${String(line)}`)
                    );
                } else if (val != null) {
                    parts.push(`${code}: ${String(val)}`);
                }
            }
        );
    }

    const joined = Array.from(new Set(parts.filter(Boolean))).join('\n');
    return joined || 'Error al importar';
};

export type IvrTypificationImportHttpError = {
    status: number;
    body: unknown;
};

const appendIfTruthy = (
    params: Record<string, string | number | boolean>,
    key: string,
    value?: string
) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
        params[key] = String(value).trim();
    }
};

export const asyncGetIvrTypificationBase = async (
    filters: ISelectIvrTypificationBase
) => {
    const params: Record<string, string | number | boolean> = {
        limit: filters.limit ?? 20,
        page: filters.page ?? 0,
    };

    appendIfTruthy(params, 'initialProcessName', filters.initialProcessName);
    appendIfTruthy(params, 'processCode', filters.processCode);
    appendIfTruthy(
        params,
        'typificationType',
        filters.typificationType as string | undefined
    );
    appendIfTruthy(params, 'typificationCode', filters.typificationCode);
    appendIfTruthy(params, 'initialTypification', filters.initialTypification);
    appendIfTruthy(params, 'subTypificationCode', filters.subTypificationCode);
    appendIfTruthy(
        params,
        'initialSubTypificationName',
        filters.initialSubTypificationName
    );
    appendIfTruthy(params, 'pqrstType', filters.pqrstType);
    appendIfTruthy(params, 'customerType', filters.customerType);
    appendIfTruthy(params, 'responseTime', filters.responseTime);

    if (filters.active === 'true' || filters.active === 'false') {
        params.active = filters.active === 'true';
    }

    return await apiService.get('/api/ivr-typification-base', params);
};

export const asyncPostIvrTypificationBase = async (
    formdata: FormData
): Promise<any> => {
    const url = `${LOCALHOST || ''}/api/ivr-typification-base`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            accept: '*/*',
            Authorization: 'Bearer ' + sessionStorage.getItem(TOKEN),
        },
        body: formdata,
    });

    let data: any;
    try {
        data = await response.json();
    } catch {
        data = null;
    }

    if (!response.ok) {
        if (response.status === 401 && window.location.pathname !== SIGNIN) {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = SIGNIN;
        }
        const err: IvrTypificationImportHttpError = {
            status: response.status,
            body: data,
        };
        throw err;
    }

    return data;
};

export const asyncGetTemplateIvrTypificationBase = async (
    query: IExportTemplateIvrTypificationBase
) => {
    return await apiService.get('/api/ivr-typification-base/template', query);
};

export type IvrTypificationActiveHttpError = {
    status: number;
    body: unknown;
};

export type IvrTypificationSyncHttpError = {
    status: number;
    body: unknown;
};

export const asyncPatchIvrTypificationBaseActive = async (
    id: string,
    active: boolean
): Promise<any> => {
    const url = `${LOCALHOST || ''}/api/ivr-typification-base/${id}/active`;

    const response = await fetch(url, {
        method: 'PATCH',
        headers: {
            accept: '*/*',
            Authorization: 'Bearer ' + sessionStorage.getItem(TOKEN),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ active }),
    });

    let data: any;
    try {
        data = await response.json();
    } catch {
        data = null;
    }

    if (!response.ok) {
        if (response.status === 401 && window.location.pathname !== SIGNIN) {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = SIGNIN;
        }

        const err: IvrTypificationActiveHttpError = {
            status: response.status,
            body: data,
        };
        throw err;
    }

    return data;
};

export const asyncSyncIvrTypificationBaseToCrmware = async (): Promise<any> => {
    const url = `${LOCALHOST || ''}/api/ivr-typification-base/sync-to-crmware`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            accept: '*/*',
            Authorization: 'Bearer ' + sessionStorage.getItem(TOKEN),
        },
    });

    let data: any;
    try {
        data = await response.json();
    } catch {
        data = null;
    }

    if (!response.ok) {
        if (response.status === 401 && window.location.pathname !== SIGNIN) {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = SIGNIN;
        }

        const err: IvrTypificationSyncHttpError = {
            status: response.status,
            body: data,
        };
        throw err;
    }

    return data;
};
